import { useMemo, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Navigate, Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { ADMIN_PORTAL_PATH } from '@/lib/adminPortal'
import {
  useAdminStreamingReport,
  type StreamingReportStats,
} from '@/hooks/useAdminStreamingReport'

function VisualDateField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) {
  const selected = value ? parseISO(value) : null
  return (
    <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
      {label}
      <DatePicker
        selected={selected}
        onChange={(date) => {
          if (!date) return
          onChange(format(date, 'yyyy-MM-dd'))
        }}
        dateFormat="yyyy-MM-dd"
        className="block mt-1 rounded px-2 py-1 border"
        popperPlacement="bottom-start"
        style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
      />
    </label>
  )
}

function StatsTable({
  columns,
  rows,
  ariaLabel,
}: {
  columns: { key: string; label: string; align?: 'right' }[]
  rows: Record<string, string | number>[]
  ariaLabel: string
}) {
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="min-w-full text-sm border-collapse" aria-label={ariaLabel}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`py-2 px-3 text-left font-semibold ${c.align === 'right' ? 'text-right' : ''}`}
                style={{ color: 'var(--text-secondary)' }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`py-2 px-3 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}
                  style={{ color: 'var(--text-primary)' }}
                >
                  {row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function statsCols(s: StreamingReportStats) {
  return {
    total: s.total,
    ad: s.withAd,
    subs: s.withSubtitles,
    bsl: s.withBsl,
    anyAcc: s.withAnyAccessibility,
    none: s.withNone,
  }
}

export default function AdminStreamingReportPage() {
  const { user } = useAuth()
  const isAdmin = useAdmin()
  const today = useMemo(() => new Date(), [])
  const defaultStart = useMemo(
    () => format(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0)), 'yyyy-MM-dd'),
    [today]
  )
  const defaultEnd = useMemo(() => {
    const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0))
    end.setUTCDate(end.getUTCDate() + 90)
    return format(end, 'yyyy-MM-dd')
  }, [today])

  const [includeExpired, setIncludeExpired] = useState(false)
  const [expiresFrom, setExpiresFrom] = useState('')
  const [expiresTo, setExpiresTo] = useState('')

  const { data, isLoading, error, refetch, isFetching } = useAdminStreamingReport(Boolean(user && isAdmin), {
    includeExpired,
    expiresFrom: expiresFrom || null,
    expiresTo: expiresTo || null,
  })

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6" role="status">
        <p style={{ color: 'var(--text-secondary)' }}>Loading streaming report…</p>
      </div>
    )
  }

  if (error) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Streaming Report
        </h1>
        <p role="alert" style={{ color: 'var(--color-error)' }}>
          {error instanceof Error ? error.message : 'Failed to load report.'}
        </p>
      </section>
    )
  }

  const report = data!

  const accCols = [
    { key: 'name', label: 'Dimension' },
    { key: 'total', label: 'Rows', align: 'right' as const },
    { key: 'ad', label: 'AD', align: 'right' as const },
    { key: 'subs', label: 'Subtitles', align: 'right' as const },
    { key: 'bsl', label: 'BSL', align: 'right' as const },
    { key: 'anyAcc', label: 'Any a11y', align: 'right' as const },
    { key: 'none', label: 'No flags', align: 'right' as const },
  ]

  const expiryDayRows = report.byExpiryDay.map((d) => ({
    name: format(parseISO(`${d.dayKey}T12:00:00.000Z`), 'EEE d MMM yyyy'),
    ...statsCols(d.stats),
  }))

  const serviceRows = report.byService.map((x) => ({
    name: x.service,
    ...statsCols(x.stats),
  }))

  const contentRows = report.byContentType.map((x) => ({
    name: x.contentType,
    ...statsCols(x.stats),
  }))

  const accessRows = report.byAccessModel.map((x) => ({
    name: x.accessModel,
    ...statsCols(x.stats),
  }))

  const o = statsCols(report.overall)

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Streaming Report
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Counts from <code className="text-xs">streaming_listings</code>. Rows are grouped by{' '}
            <strong>catalogue expiry</strong> (UTC): when the row drops off the public Streaming page unless re-ingested.{' '}
            <Link className="underline" style={{ color: 'var(--color-focus)' }} to={`${ADMIN_PORTAL_PATH}/listings-management#streaming`}>
              Streaming catalogue
            </Link>{' '}
            — upload and window days.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="px-4 py-2 rounded text-sm font-semibold shrink-0"
          style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div
        className="mb-6 p-4 rounded-lg border-2 flex flex-wrap items-end gap-4"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
      >
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
            className="rounded"
          />
          <span style={{ color: 'var(--text-primary)' }}>Include expired rows</span>
        </label>
        <VisualDateField label="Expires on or after (UTC)" value={expiresFrom} onChange={setExpiresFrom} />
        <VisualDateField label="Expires on or before (UTC)" value={expiresTo} onChange={setExpiresTo} />
        <button
          type="button"
          onClick={() => {
            setExpiresFrom(defaultStart)
            setExpiresTo(defaultStart)
          }}
          className="px-3 py-2 rounded text-sm font-semibold border"
          style={{
            borderColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            backgroundColor: 'var(--bg-primary)',
          }}
        >
          Today (expiry range)
        </button>
      </div>

      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        {report.windowLabel} · <strong style={{ color: 'var(--text-primary)' }}>{report.rowCount}</strong> row(s) in sample.
      </p>

      <section
        className="mb-8 p-4 rounded-lg border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
      >
        <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Overall
        </h2>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>Rows</dt>
            <dd className="text-2xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{o.total}</dd>
          </div>
          <div>
            <dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>AD</dt>
            <dd className="text-2xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{o.ad}</dd>
          </div>
          <div>
            <dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>Subtitles</dt>
            <dd className="text-2xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{o.subs}</dd>
          </div>
          <div>
            <dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>BSL</dt>
            <dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{o.bsl}</dd>
          </div>
          <div>
            <dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>Any accessibility</dt>
            <dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{o.anyAcc}</dd>
          </div>
          <div>
            <dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>No flags</dt>
            <dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{o.none}</dd>
          </div>
        </dl>
      </section>

      <section className="mb-8 p-4 rounded-lg border-2" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}>
        <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          By catalogue expiry day (UTC)
        </h2>
        {expiryDayRows.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No rows in this filter.</p>
        ) : (
          <StatsTable columns={accCols} rows={expiryDayRows} ariaLabel="Streaming counts by expiry day" />
        )}
      </section>

      <section className="mb-8 p-4 rounded-lg border-2" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}>
        <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          By service
        </h2>
        <StatsTable columns={accCols} rows={serviceRows} ariaLabel="Streaming counts by service" />
      </section>

      <section className="mb-8 p-4 rounded-lg border-2" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}>
        <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          By content type
        </h2>
        <StatsTable columns={accCols} rows={contentRows} ariaLabel="Streaming counts by content type" />
      </section>

      <section className="mb-8 p-4 rounded-lg border-2" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}>
        <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          By access model
        </h2>
        <StatsTable columns={accCols} rows={accessRows} ariaLabel="Streaming counts by access model" />
      </section>
    </div>
  )
}
